/**
 * InteractiveCanvasPreview
 *
 * Div-based preview for step 2 (frame selection) with:
 * - Click to select a slot (shows selection border + handles)
 * - Drag to pan photo within slot
 * - Scroll/pinch to zoom
 * - Corner handles for resize
 * - Rotation handle
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { LocalFrame } from '../../../lib/db/schema';
import type { EditPhotoMinimal, PhotoTransform } from '../types/edit-session';
import { getLayoutById, getAspectRatioForLayout, SLOT_GAP_PERCENT } from '../types/canvas-layouts';

const MIN_SCALE = 0.25;
const MAX_SCALE = 3;

export interface InteractiveCanvasPreviewProps {
  canvasIndex: number;
  layoutId: string;
  slotOffset: number;
  photoArrangement: Map<number, string>;
  photos: EditPhotoMinimal[];
  frame: LocalFrame | null;
  photoTransforms: Map<number, PhotoTransform>;
  onTransformChange: (globalSlotIndex: number, transform: PhotoTransform) => void;
  className?: string;
}

function defaultTransform(): PhotoTransform {
  return { offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
}

function getT(m: Map<number, PhotoTransform>, idx: number): PhotoTransform {
  return m.get(idx) ?? defaultTransform();
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/** Interaction state tracked via ref (not state) for perf during drag. */
interface Interaction {
  type: 'pan' | 'resize' | 'rotate';
  globalSlotIndex: number;
  startX: number;
  startY: number;
  startTransform: PhotoTransform;
  slotW: number;
  slotH: number;
  centerX: number; // slot center in screen coords
  centerY: number;
  startDist: number; // for resize
  startAngle: number; // for rotate (radians)
}

export function InteractiveCanvasPreview({
  canvasIndex,
  layoutId,
  slotOffset,
  photoArrangement,
  photos,
  frame,
  photoTransforms,
  onTransformChange,
  className = '',
}: InteractiveCanvasPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [slotNaturalSizes, setSlotNaturalSizes] = useState<Map<number, { w: number; h: number }>>(new Map());
  const interactionRef = useRef<Interaction | null>(null);

  // Keep latest callbacks in refs so window listeners don't go stale
  const onTransformRef = useRef(onTransformChange);
  onTransformRef.current = onTransformChange;
  const transformsRef = useRef(photoTransforms);
  transformsRef.current = photoTransforms;

  const layout = getLayoutById(layoutId);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setContainerSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    const { width, height } = el.getBoundingClientRect();
    if (width > 0 && height > 0) setContainerSize({ w: width, h: height });
    return () => ro.disconnect();
  }, [layoutId]);

  /** Get slot pixel rect relative to container (with gap applied). */
  const slotRect = useCallback(
    (localIdx: number) => {
      if (!layout || !containerSize) return null;
      const s = layout.slots[localIdx];
      if (!s) return null;
      const half = SLOT_GAP_PERCENT / 2;
      const x = s.x + half;
      const y = s.y + half;
      const w = Math.max(0, s.width - SLOT_GAP_PERCENT);
      const h = Math.max(0, s.height - SLOT_GAP_PERCENT);
      return {
        l: (x / 100) * containerSize.w,
        t: (y / 100) * containerSize.h,
        w: (w / 100) * containerSize.w,
        h: (h / 100) * containerSize.h,
      };
    },
    [layout, containerSize],
  );

  /** Slot center in screen coords. */
  const slotScreenCenter = useCallback(
    (localIdx: number) => {
      const el = containerRef.current;
      const sr = slotRect(localIdx);
      if (!el || !sr) return null;
      const cr = el.getBoundingClientRect();
      return { x: cr.left + sr.l + sr.w / 2, y: cr.top + sr.t + sr.h / 2 };
    },
    [slotRect],
  );

  // ---------- Interaction helpers ----------

  const startPan = (globalIdx: number, localIdx: number, e: React.PointerEvent) => {
    const sr = slotRect(localIdx);
    const sc = slotScreenCenter(localIdx);
    if (!sr || !sc) return;
    interactionRef.current = {
      type: 'pan',
      globalSlotIndex: globalIdx,
      startX: e.clientX,
      startY: e.clientY,
      startTransform: getT(transformsRef.current, globalIdx),
      slotW: sr.w,
      slotH: sr.h,
      centerX: sc.x,
      centerY: sc.y,
      startDist: 0,
      startAngle: 0,
    };
  };

  const startResize = (globalIdx: number, localIdx: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sr = slotRect(localIdx);
    const sc = slotScreenCenter(localIdx);
    if (!sr || !sc) return;
    const dist = Math.hypot(e.clientX - sc.x, e.clientY - sc.y);
    interactionRef.current = {
      type: 'resize',
      globalSlotIndex: globalIdx,
      startX: e.clientX,
      startY: e.clientY,
      startTransform: getT(transformsRef.current, globalIdx),
      slotW: sr.w,
      slotH: sr.h,
      centerX: sc.x,
      centerY: sc.y,
      startDist: dist || 1,
      startAngle: 0,
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const startRotate = (globalIdx: number, localIdx: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sr = slotRect(localIdx);
    const sc = slotScreenCenter(localIdx);
    if (!sr || !sc) return;
    const angle = Math.atan2(e.clientY - sc.y, e.clientX - sc.x);
    interactionRef.current = {
      type: 'rotate',
      globalSlotIndex: globalIdx,
      startX: e.clientX,
      startY: e.clientY,
      startTransform: getT(transformsRef.current, globalIdx),
      slotW: sr.w,
      slotH: sr.h,
      centerX: sc.x,
      centerY: sc.y,
      startDist: 0,
      startAngle: angle,
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  // Global move/up listeners (ref-based, no stale closures)
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const ix = interactionRef.current;
      if (!ix) return;
      const fn = onTransformRef.current;
      const prev = ix.startTransform;

      if (ix.type === 'pan') {
        const dx = (e.clientX - ix.startX) / ix.slotW;
        const dy = (e.clientY - ix.startY) / ix.slotH;
        fn(ix.globalSlotIndex, { ...prev, offsetX: prev.offsetX + dx, offsetY: prev.offsetY + dy });
      } else if (ix.type === 'resize') {
        const dist = Math.hypot(e.clientX - ix.centerX, e.clientY - ix.centerY);
        const ratio = dist / ix.startDist;
        fn(ix.globalSlotIndex, { ...prev, scale: clamp(prev.scale * ratio, MIN_SCALE, MAX_SCALE) });
      } else if (ix.type === 'rotate') {
        const angle = Math.atan2(e.clientY - ix.centerY, e.clientX - ix.centerX);
        const delta = ((angle - ix.startAngle) * 180) / Math.PI;
        fn(ix.globalSlotIndex, { ...prev, rotation: prev.rotation + delta });
      }
    };
    const onUp = (e: PointerEvent) => {
      if (interactionRef.current && e.target instanceof HTMLElement && typeof e.target.releasePointerCapture === 'function') {
        e.target.releasePointerCapture(e.pointerId);
      }
      interactionRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  // Wheel to zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      if (activeSlot === null) return;
      const t = getT(photoTransforms, activeSlot);
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      onTransformChange(activeSlot, { ...t, scale: clamp(t.scale + delta, MIN_SCALE, MAX_SCALE) });
    },
    [activeSlot, onTransformChange, photoTransforms],
  );

  // Slot click: activate or start pan
  const handleSlotDown = (globalIdx: number, localIdx: number, e: React.PointerEvent) => {
    e.stopPropagation();
    if (activeSlot === globalIdx) {
      startPan(globalIdx, localIdx, e);
    } else {
      setActiveSlot(globalIdx);
    }
  };

  // Background click: deactivate
  const handleBgDown = useCallback(() => setActiveSlot(null), []);

  // Frame URL: create in effect, revoke in delayed cleanup so frame image never breaks
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!frame?.imageBlob || !(frame.imageBlob instanceof Blob)) {
      setFrameUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(frame.imageBlob);
    setFrameUrl(objectUrl);
    return () => {
      const toRevoke = objectUrl;
      setTimeout(() => URL.revokeObjectURL(toRevoke), 0);
    };
  }, [frame?.frameId]);

  // Active slot local index
  const activeLocal = activeSlot !== null ? activeSlot - slotOffset : null;

  if (!layout) return null;

  return (
    <div className={`relative ${className}`} style={{ aspectRatio: layout ? getAspectRatioForLayout(layout) : '2/3', maxWidth: '100%', maxHeight: '100%', width: '100%' }}>
      <div
        ref={containerRef}
        className="relative w-full h-full rounded-lg border border-gray-200 overflow-visible"
        onPointerDown={handleBgDown}
        onWheel={handleWheel}
        style={{ touchAction: 'none' }}
      >
        {/* Clipped area for slots + frame so photos don't spill; selection can overflow */}
        <div className="absolute inset-0 bg-gray-100 rounded-lg overflow-hidden">
          {/* Photo slots */}
          {layout.slots.map((slot, localIdx) => {
          const gIdx = slotOffset + localIdx;
          const photoId = photoArrangement.get(gIdx);
          const photo: EditPhotoMinimal | null = photoId
            ? photos.find((p) => p.photoId === photoId || (p as { id?: string }).id === photoId) ?? null
            : null;
          const t = getT(photoTransforms, gIdx);
          const half = SLOT_GAP_PERCENT / 2;
          const slotLeft = slot.x + half;
          const slotTop = slot.y + half;
          const slotW = Math.max(0, slot.width - SLOT_GAP_PERCENT);
          const slotH = Math.max(0, slot.height - SLOT_GAP_PERCENT);
          return (
            <div
              key={gIdx}
              className="absolute"
              style={{ left: `${slotLeft}%`, top: `${slotTop}%`, width: `${slotW}%`, height: `${slotH}%` }}
            >
              {/* Photo (clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ cursor: activeSlot === gIdx ? 'grab' : 'pointer' }}
                onPointerDown={(e) => handleSlotDown(gIdx, localIdx, e)}
              >
                {photo ? (
                  <SlotImage
                    photo={photo}
                    transform={t}
                    containerSize={containerSize}
                    slot={{ x: slotLeft, y: slotTop, width: slotW, height: slotH }}
                    onNaturalLoad={(w, h) =>
                      setSlotNaturalSizes((prev) => new Map(prev).set(gIdx, { w, h }))
                    }
                  />
                ) : (
                  <div className="absolute inset-0 bg-gray-200 border border-gray-300" />
                )}
              </div>
            </div>
          );
        })}

          {/* Frame overlay */}
          {frameUrl && (
            <img src={frameUrl} alt="" className="absolute inset-0 w-full h-full pointer-events-none" draggable={false} aria-hidden />
          )}
        </div>

        {/* Selection handles (above frame) – around the photo image, not the full slot */}
        {activeLocal !== null && containerSize && (() => {
          const sr = slotRect(activeLocal);
          if (!sr) return null;
          const gIdx = activeSlot!;
          const t = getT(photoTransforms, gIdx);
          const nat = slotNaturalSizes.get(gIdx);
          // Photo drawn rect: same math as SlotImage (in slot coords then offset by slot position)
          let left = 0;
          let top = 0;
          let w = sr.w;
          let h = sr.h;
          if (nat && nat.w > 0 && nat.h > 0) {
            const base = Math.max(sr.w / nat.w, sr.h / nat.h);
            const s = base * t.scale;
            w = nat.w * s;
            h = nat.h * s;
            left = sr.w / 2 - w / 2 + t.offsetX * sr.w;
            top = sr.h / 2 - h / 2 + t.offsetY * sr.h;
          }
          const photoL = sr.l + left;
          const photoT = sr.t + top;
          const rotation = t.rotation ?? 0;
          // Match SlotImage: photo rotates around slot center, not photo center
          const originX = sr.w / 2 - left;
          const originY = sr.h / 2 - top;
          return (
            <div
              className="absolute"
              style={{
                left: photoL,
                top: photoT,
                width: w,
                height: h,
                zIndex: 30,
                pointerEvents: 'none',
                transformOrigin: `${originX}px ${originY}px`,
                transform: `rotate(${rotation}deg)`,
              }}
            >
              {/* Selection border (no pointer events so clicks fall through to slot) */}
              <div className="absolute inset-0 border-2 border-blue-500 rounded-sm" style={{ pointerEvents: 'none' }} />
              {/* Corner handles – need pointer events so use a wrapper that resets it */}
              {CORNERS.map((c) => (
                <div
                  key={c.name}
                  className="absolute bg-white border-2 border-blue-500 rounded-sm"
                  style={{
                    width: 10,
                    height: 10,
                    cursor: c.cursor,
                    left: c.left,
                    top: c.top,
                    transform: 'translate(-50%,-50%)',
                    pointerEvents: 'auto',
                  }}
                  onPointerDown={(e) => startResize(gIdx, activeLocal, e)}
                />
              ))}
              {/* Rotation handle above the photo */}
              <div className="absolute" style={{ left: '50%', top: -28, transform: 'translateX(-50%)', pointerEvents: 'auto' }}>
                <div style={{ width: 2, height: 16, background: '#3b82f6', margin: '0 auto' }} />
                <div
                  className="bg-white border-2 border-blue-500 rounded-full"
                  style={{ width: 14, height: 14, cursor: 'grab', margin: '-2px auto 0' }}
                  onPointerDown={(e) => startRotate(gIdx, activeLocal, e)}
                />
              </div>
            </div>
          );
        })()}
      </div>
      <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
        Photo {canvasIndex + 1}
      </div>
    </div>
  );
}

const CORNERS = [
  { name: 'tl', cursor: 'nwse-resize', left: '0%', top: '0%' },
  { name: 'tr', cursor: 'nesw-resize', left: '100%', top: '0%' },
  { name: 'bl', cursor: 'nesw-resize', left: '0%', top: '100%' },
  { name: 'br', cursor: 'nwse-resize', left: '100%', top: '100%' },
] as const;

// ---------- SlotImage sub-component ----------

interface SlotImageProps {
  photo: EditPhotoMinimal;
  transform: PhotoTransform;
  containerSize: { w: number; h: number } | null;
  slot: { x: number; y: number; width: number; height: number };
  onNaturalLoad?: (width: number, height: number) => void;
}

function SlotImage({ photo, transform, containerSize, slot, onNaturalLoad }: SlotImageProps) {
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      setNat({ w: nw, h: nh });
      onNaturalLoad?.(nw, nh);
    },
    [onNaturalLoad],
  );

  // Create object URL in effect; revoke in delayed cleanup so we never revoke while img still uses it
  useEffect(() => {
    if (!photo?.imageBlob || !(photo.imageBlob instanceof Blob)) return;
    const objectUrl = URL.createObjectURL(photo.imageBlob);
    setUrl(objectUrl);
    return () => {
      const toRevoke = objectUrl;
      setTimeout(() => URL.revokeObjectURL(toRevoke), 0);
    };
  }, [photo?.photoId]);

  const slotW = containerSize ? (slot.width / 100) * containerSize.w : 0;
  const slotH = containerSize ? (slot.height / 100) * containerSize.h : 0;

  if (!url) return null;

  if (!nat || slotW === 0 || slotH === 0) {
    return (
      <img
        src={url}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        onLoad={handleLoad}
        draggable={false}
      />
    );
  }

  const base = Math.max(slotW / nat.w, slotH / nat.h);
  const s = base * transform.scale;
  const w = nat.w * s;
  const h = nat.h * s;
  const left = slotW / 2 - w / 2 + transform.offsetX * slotW;
  const top = slotH / 2 - h / 2 + transform.offsetY * slotH;

  // Rotation origin = slot center relative to img
  const originX = slotW / 2 - left;
  const originY = slotH / 2 - top;

  return (
    <img
      src={url}
      alt=""
      draggable={false}
      style={{
        position: 'absolute',
        left,
        top,
        width: w,
        height: h,
        transform: transform.rotation ? `rotate(${transform.rotation}deg)` : undefined,
        transformOrigin: `${originX}px ${originY}px`,
        pointerEvents: 'none',
      }}
    />
  );
}
